import { isHero, makeCopy } from "@/services/Helpers";
import UnitService from "@/services/UnitService";
import UpgradeService from "@/services/UpgradeService";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { nanoid } from "nanoid";
import { ISelectedUnit, IUnit, IUpgradeOption, IUpgradeSection } from "../types";
import { resetList } from "./listSlice";

interface UnitsState {
  units: ISelectedUnit[];
  undoUnitRemove?: ISelectedUnit[];
  selectedUnitId?: string;
  unitPreview?: ISelectedUnit;
}

const initialState: UnitsState = {
  units: [],
  selectedUnitId: null,
  undoUnitRemove: null,
  unitPreview: null
};

const HERO_JOIN_MAX_TOUGH = 6;
const detachHeroIfTooToughToJoin = (unit: ISelectedUnit) => {
  if (!unit?.joinToUnit || !isHero(unit)) return;
  if (UnitService.getTough(unit) > HERO_JOIN_MAX_TOUGH) {
    unit.joinToUnit = null;
  }
};

export const addUnit = createAsyncThunk("units/addUnit", async (unit: IUnit) => {
  const selectedUnit = UnitService.createUnitFromDefinition(unit);
  return UpgradeService.buildUpgrades(selectedUnit);
});

const listSlice = createSlice({
  name: "units",
  initialState,
  reducers: {
    resetUnits() {
      return initialState;
    },
    loadSavedUnits(state, action: PayloadAction<ISelectedUnit[]>) {
      const units = action.payload;
      return {
        ...initialState,
        units
      };
    },
    addCombinedUnit(state, action: PayloadAction<string>) {
      const parentindex = state.units.findIndex((t) => action.payload == t.selectionId);

      const parentUnit = state.units[parentindex];
      parentUnit.combined = true;

      const newUnit: ISelectedUnit = {
        ...parentUnit,
        selectionId: nanoid(5),
        selectedUpgrades: parentUnit.selectedUpgrades.filter((x) => {
          if (x.upgrade.isCommandGroup) return false;
          if (x.upgrade.variant === "upgrade" && x.upgrade.affects?.value === 1) return false;

          return true;
        }),
        joinToUnit: parentUnit.selectionId
      };

      const finalUnit = UpgradeService.buildUpgrades(newUnit);

      state.units.splice(parentindex + 1, 0, finalUnit);
    },
    addUnits(state, action: PayloadAction<ISelectedUnit[]>) {
      const units = action.payload;
      let unitsMapped = units.map((u) => ({
        ...u,
        selectionId: nanoid(5)
      }));

      units.forEach((u, i) => {
        if (u.joinToUnit) {
          let joinedIndex = units.findIndex((t) => {
            return t.selectionId === u.joinToUnit;
          });
          if (joinedIndex >= 0) {
            unitsMapped[i].joinToUnit = unitsMapped[joinedIndex].selectionId;
          } else {
            unitsMapped[i].joinToUnit = null;
            unitsMapped[i].combined = false;
          }
        }
        if (u.combined) {
          unitsMapped[i].combined = units.some((t) => {
            return t.selectionId === u.joinToUnit || t.joinToUnit === u.selectionId;
          });
        }
      });

      state.units.push(...unitsMapped);
    },
    selectUnit(state, action: PayloadAction<string>) {
      state.selectedUnitId = action.payload;
      state.unitPreview = null;
    },
    removeUnit(state, action: PayloadAction<string>) {
      const unitId = action.payload;
      const removeIndex = state.units.findIndex((u) => u.selectionId === unitId);

      if (removeIndex >= 0) {
        let unit = state.units[removeIndex];

        if (unit.combined) {
          if (!unit.joinToUnit) {
            state.undoUnitRemove = state.units.splice(removeIndex, 1);
            let childIndex = state.units.findIndex((t) => {
              return t.combined && t.joinToUnit === unitId;
            });
            if (childIndex !== -1) {
              state.undoUnitRemove = state.undoUnitRemove.concat(state.units.splice(childIndex, 1));
            }
          } else {
            let parent = state.units.find((t) => {
              return t.combined && unit.joinToUnit === t.selectionId;
            });
            if (parent) {
              parent.combined = false;
            }
            // don't bother saving it in the undoRemove stuff.
            state.units.splice(removeIndex, 1);
          }
        } else {
          state.undoUnitRemove = state.units.splice(removeIndex, 1);
        }

        for (let unit of state.units) {
          if (unit.joinToUnit === unitId) unit.joinToUnit = null;
        }
      }
    },
    undoRemoveUnit(state) {
      state.units = state.units.concat(state.undoUnitRemove);

      state.undoUnitRemove = null;
    },
    renameUnit(state, action: PayloadAction<{ unitId: string; name: string }>) {
      const { unitId, name } = action.payload;
      const unit = state.units.filter((u) => u.selectionId === unitId)[0];
      unit.customName = name;
      if (unit.combined) {
        let partner = state.units.find(
          (t) => t.selectionId === unit.joinToUnit || (t.combined && t.joinToUnit === unitId)
        );
        partner.customName = name;
      }
    },
    moveUnit(state, action: PayloadAction<{ from: number; to: number }>) {
      let { from, to } = action.payload;
      to = to <= from ? to : to - 1;
      if (from == to) return;
      const unit = state.units[from];
      state.units.splice(from, 1);
      state.units.splice(to, 0, unit);
    },
    joinUnit(state, action: PayloadAction<{ unitId: string; joinToUnitId: string | null }>) {
      const { unitId, joinToUnitId } = action.payload;
      const unit = state.units.filter((u) => u.selectionId === unitId)[0];

      unit.joinToUnit = joinToUnitId;
    },
    applyUpgrade(
      state,
      action: PayloadAction<{ unitId: string; upgrade: IUpgradeSection; option: IUpgradeOption }>
    ) {
      // TODO: Refactor, break down, unit test...
      const { unitId, upgrade, option } = action.payload;
      const unit = state.units.filter((u) => u.selectionId === unitId)[0];

      const newInstanceId = UpgradeService.apply(unit, upgrade, option);
      detachHeroIfTooToughToJoin(unit);

      if (unit.combined && upgrade.affects?.type === "all") {
        const partner = state.units.find(
          (t) => t.selectionId == unit.joinToUnit || (t.combined && t.joinToUnit == unit.selectionId)
        );
        UpgradeService.apply(partner, upgrade, option, newInstanceId);
      }
    },
    removeUpgrade: (
      state,
      action: PayloadAction<{ unitId: string; upgrade: IUpgradeSection; option: IUpgradeOption }>
    ) => {
      // TODO: Refactor, break down, unit test...

      const { unitId, upgrade, option } = action.payload;
      const unit = state.units.filter((u) => u.selectionId === unitId)[0];

      UpgradeService.remove(unit, option);
      if (unit.combined && upgrade.affects?.type === "all") {
        const partner = state.units.find(
          (t) => t.selectionId == unit.joinToUnit || (t.combined && t.joinToUnit == unit.selectionId)
        );
        UpgradeService.remove(partner, option);
      }
    },
    replaceUpgrade: (
      state,
      action: PayloadAction<{
        unitId: string;
        replaceInstanceId: string;
        upgrade: IUpgradeSection;
        option: IUpgradeOption;
      }>
    ) => {
      const { unitId, replaceInstanceId, upgrade, option } = action.payload;
      const unit = state.units.filter((u) => u.selectionId === unitId)[0];

      const apply = (unit: ISelectedUnit, instanceId?: string) => {
        const insertIndex = unit.selectedUpgrades.findLastIndex((x) => x.instanceId === replaceInstanceId);
        UpgradeService.removeById(unit, replaceInstanceId);
        const newInstanceId = UpgradeService.apply(unit, upgrade, option, instanceId, insertIndex);
        detachHeroIfTooToughToJoin(unit);
        return newInstanceId;
      };

      const newInstanceId = apply(unit);

      if (unit.combined && upgrade.affects?.type === "all") {
        const partner = state.units.find(
          (t) => t.selectionId == unit.joinToUnit || (t.combined && t.joinToUnit == unit.selectionId)
        );
        apply(partner, newInstanceId);
      }
    },
    removeUnitsForBook(state, action: PayloadAction<string>) {
      const armyBookId = action.payload;
      state.units = state.units.filter((unit) => unit.armyId !== armyBookId);
    },
    adjustXp(state, action: PayloadAction<{ unitId: string; xp: number }>) {
      const { unitId, xp } = action.payload;
      const unit = state.units.find((u) => u.selectionId === unitId);
      if (!unit.xp) unit.xp = 0;
      unit.xp += xp;
    },
    toggleTrait(state, action: PayloadAction<{ unitId: string; trait: string }>) {
      const { unitId, trait } = action.payload;
      const unit = state.units.find((u) => u.selectionId === unitId);
      const existingTraitIndex = unit.traits.findIndex((t) => t === trait);
      if (existingTraitIndex >= 0) {
        unit.traits.splice(existingTraitIndex, 1);
      } else {
        unit.traits.push(trait);
      }
    },
    setUnitNotes(state, action: PayloadAction<{ unitId: string; notes: string }>) {
      const { unitId, notes } = action.payload;
      const unit = state.units.find((u) => u.selectionId === unitId);
      unit.notes = notes;
    },

    previewUnit(state, action: PayloadAction<ISelectedUnit>) {
      const unit: ISelectedUnit = makeCopy(action.payload);
      unit.selectedUpgrades = [];
      state.unitPreview = UpgradeService.buildUpgrades(unit);
      state.selectedUnitId = null;
    }
  },
  extraReducers: (builder) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder
      .addCase(addUnit.fulfilled, (state, action: PayloadAction<ISelectedUnit>) => {
        const unit = action.payload;
        state.units.push(unit);
        state.selectedUnitId = unit.selectionId;
        state.unitPreview = null;
      })
      .addCase(resetList, () => {
        return initialState;
      });
  }
});

// Action creators are generated for each case reducer function
export const {
  loadSavedUnits,
  applyUpgrade,
  removeUpgrade,
  replaceUpgrade,
  addCombinedUnit,
  addUnits,
  selectUnit,
  removeUnit,
  renameUnit,
  moveUnit,
  joinUnit,
  undoRemoveUnit,
  removeUnitsForBook,
  previewUnit,
  adjustXp,
  toggleTrait,
  setUnitNotes
} = listSlice.actions;

export default listSlice.reducer;
